import PropTypes from "prop-types";
import { Box } from "@mui/material";

// base on-screen size for a contour (percent of the "ContourStage"), before the
// per-body-part multiplier below.
const CONTOUR_MAX_WIDTH_PCT = 120;
const CONTOUR_MAX_HEIGHT_PCT = 120;
// the default aspect ratio of the "ContourStage".
const STAGE_ASPECT = 1170 / 2532;

// A fixed-aspect box centered on the screen, the largest that fits. The body
// contour and the reference contour are both sized and placed in percentages of
// it, so their relative position stays the same on every screen size/aspect
// (browser toolbars, fullscreen, ...) instead of drifting with the viewport.
export const ContourStage = ({ children }) => (
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      width: `min(100vw, calc(100dvh * ${STAGE_ASPECT}))`,
      aspectRatio: `${STAGE_ASPECT}`,
      transform: "translate(-50%, -50%)",
      pointerEvents: "none",
    }}
  >
    {children}
  </Box>
);

ContourStage.propTypes = { children: PropTypes.node };

// per-body-part placement: the contour's center as viewport percentages
// (x from the left, y from the top) plus a size multiplier. This is the
// default placement for defined body parts.
const CONTOUR_PLACEMENT = {
  fo: { x: 50, y: 48, scale: 0.5 },
  afo: { x: 50, y: 48, scale: 1.1 },
  kafo: { x: 50, y: 48, scale: 1.1 },
  hand: { x: 50, y: 48, scale: 1 },
  torso: { x: 50, y: 48, scale: 1 },
  head: { x: 50, y: 48, scale: 1 },
  "lower-body": { x: 50, y: 48, scale: 0.9 },
  "full-body": { x: 50, y: 48, scale: 1 },
  shoe: { x: 50, y: 50, scale: 0.9 },
  "both-shoes": { x: 50, y: 52, scale: 0.9 },
};
// placement overrides for scans without a reference object.
const CONTOUR_PLACEMENT_NO_REFERENCE = {
  fo: { x: 50, y: 48, scale: 0.5 },
};
const DEFAULT_PLACEMENT = { x: 50, y: 48, scale: 1 };

// the contour file name for a body part (lower-cased, spaces -> dashes).
function contourNameFor(bodyPart) {
  return bodyPart.trim().toLowerCase().replace(/\s+/g, "-");
}

// Flip factors of the body-part contour:
// "flipped" mode turns it upside down, the right side and the front camera
// each mirror it horizontally.
export function contourFlip({ mode, side, mirrored }) {
  const flipped = mode === "flipped";
  const rightSide = Boolean(side) && side.toLowerCase() === "right";
  return {
    flipX: (flipped ? -1 : 1) * (rightSide ? -1 : 1) * (mirrored ? -1 : 1),
    flipY: flipped ? -1 : 1,
  };
}

// Reflect a placement center across the screen center along the flipped axes.
// Contours are not always centered on screen, so flipping must also move them
// to the reflected spot.
export function flipPlacement({ x, y }, { flipX, flipY }) {
  return {
    x: flipX < 0 ? 100 - x : x,
    y: flipY < 0 ? 100 - y : y,
  };
}

// The body-part contour drawn over the live camera.
// `side` mirrors the contour for right-side scans;
// `mode` is "off" (hidden), "upright", or "flipped";
// `mirrored` flips the contour horizontally (for front camera);
// `hasReference` is true when a reference object (QR code / credit card) is
// part of the scan, which can give the contour a different placement;
// `onUnavailable` fires if the SVG fails to load (no contour for this body
// part).
export const ContourOverlay = ({
  bodyPart,
  side,
  mode,
  mirrored,
  hasReference,
  onUnavailable,
}) => {
  if (mode === "off") return null;

  const name = contourNameFor(bodyPart);
  const placement =
    (!hasReference && CONTOUR_PLACEMENT_NO_REFERENCE[name]) ||
    CONTOUR_PLACEMENT[name] ||
    DEFAULT_PLACEMENT;
  const { x, y, scale } = placement;

  const { flipX, flipY } = contourFlip({ mode, side, mirrored });
  const pos = flipPlacement({ x, y }, { flipX, flipY });

  return (
    <ContourStage>
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/contour/${name}.svg`}
        alt=""
        onError={onUnavailable}
        sx={{
          position: "absolute",
          top: `${pos.y}%`,
          left: `${pos.x}%`,
          // fit any contour within the screen
          maxWidth: `${CONTOUR_MAX_WIDTH_PCT * scale}%`,
          maxHeight: `${CONTOUR_MAX_HEIGHT_PCT * scale}%`,
          transform: `translate(-50%, -50%) scale(${flipX}, ${flipY})`,
          pointerEvents: "none",
        }}
      />
    </ContourStage>
  );
};

ContourOverlay.propTypes = {
  bodyPart: PropTypes.string.isRequired,
  side: PropTypes.string,
  mode: PropTypes.oneOf(["off", "upright", "flipped"]).isRequired,
  mirrored: PropTypes.bool,
  hasReference: PropTypes.bool,
  onUnavailable: PropTypes.func.isRequired,
};
